'use client'
import { useRef, type FC } from 'react'
import styled from 'styled-components'
import Image from 'next/image'
import { motion, useInView } from 'motion/react'
import { mq, presetsFluid, rgbAlpha, VARIANTS_FADE_FROM_RIGHT_TO_LEFT, VARIANTS_STAGGER_CHILDREN, vw } from '@/styles'
import type { TypeLang, TypeImageLocal } from '@/schema'
import { useParams } from 'next/navigation'
import { diagonalSvg } from './project/layout/AsidePreFooter'

type Props = {
  data: {
		title: string
		description: string
		medium?: TypeImageLocal
	}[]
}

const dlProps = {
  initial: 'initial',
  animate: 'animate',
  exit: 'exit',
  variants: VARIANTS_STAGGER_CHILDREN
}

export const ListInfoRows:FC<Props> = ({ data }) => {
  const { lang } = useParams() as { lang: TypeLang }
  const ref = useRef<HTMLDListElement>(null)
  const isInView = useInView(ref, { margin: '2px', once: true })

  return (
		<Dl ref={ref} {...dlProps} animate={isInView ? 'animate' : 'initial'}>
			{data.map((article, index) => (
				<motion.dd key={index} variants={VARIANTS_FADE_FROM_RIGHT_TO_LEFT(1)}>
					<h3>{article.title}</h3>
					<article dangerouslySetInnerHTML={{ __html: article.description }} />
					<div className='medium-wrapper'>
						{diagonalSvg()}
						{article.medium && (
							<Image
								src={article.medium.src}
								alt={article.medium.alt?.[lang] ?? article.medium.alt}
								width={article.medium.size.width}
								height={article.medium.size.height}
								loading='lazy'
								unoptimized={true}
							/>
						)}
					</div>
				</motion.dd>
			))}
		</Dl>
  )
}

const Dl = styled(motion.dl)`
	width: 100vw;
	width: 100dvw;

	${mq.greaterThan('desktop-xs')} {
		width: var(--viewport-width);
		width: var(--viewport-width--dynamic);
	}

  dd {
    align-items: flex-start;
    border-top: 1px solid ${rgbAlpha('--color-white', .2)};
		color: var(--color-white);
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    gap: ${vw(10, 'mobile')};
    padding: ${vw(20, 'mobile')} ${vw(10, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      gap: ${vw(10, 'tablet')};
      padding: ${vw(20, 'tablet')} ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
			display: grid;
      gap: ${vw(50, 'desktop')};
			grid-template-columns: 1fr ${vw(556, 'desktop')} ${vw(447, 'desktop')};
			grid-auto-flow: row;
			min-height: 400px;
      padding: ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      gap: 50px;
      padding: 30px;
    }

    &:first-of-type {
      ${mq.greaterThan('tablet')} {
        border-top: none;
      }
    }

    li {
      list-style: '· ';
      list-style-position: inside;
      margin-bottom: 10px;
    }
  }

	h3, article, .medium-wrapper {
		grid-column: auto / span 1;
	}

  h3, article {
    width: 100%;
  }

  h3 {
    ${presetsFluid.font25_30()}
    text-align: left;

    ${mq.greaterThan('tablet')} {
      padding-right: ${vw(10, 'desktop')};
    }

    ${mq.greaterThan('tablet')} {
      padding-right: 10px;
    }

		${mq.greaterThan('desktop')} {
			${presetsFluid.font30_rel()}
		}
  }

  article {
    opacity: .7;

    ${mq.greaterThan('tablet')} {
      padding-left: ${vw(10, 'desktop')};
			width: ${vw(556, 'desktop')};
    }

    ${mq.greaterThan('tablet')} {
      padding-left: 10px;
    }
  }

  p, ul, li, a {
    ${presetsFluid.font18()}

		${mq.greaterThan('desktop')} {
			${presetsFluid.font18_rel()}
		}
  }

  p, ul {
    margin-bottom: 8px;
  }

  .medium-wrapper {
    height: 230px;
    overflow: hidden;
    position: relative;
    width: 100%;

    ${mq.greaterThan('tablet')} {
			height: 100%;
			min-height: ${vw(340, 'desktop')};
      width: ${vw(447, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
			min-height: 340px;
      width: 447px;
    }

    img {
      height: 100%;
      object-fit: cover;
      width: 100%;
      z-index: 2;
    }
  }
`
